<template>
  <li v-if="divider" class="menu-divider"></li>
  <li
    v-else
    v-show="visible"
    :id="id"
    role="option"
    :aria-disabled="isDisabled || undefined"
    :aria-selected="itemSelected"
    :class="{
      [namespace]: true,
      'select-dropdown-item': true,
      'is-selected': itemSelected,
      'is-disabled': isDisabled,
      'hover': hover
    }"
    @mouseenter="hoverItem"
    @click.stop="selectOptionClick"
  >
    <slot>
      <div>{{ currentLabel }}</div>
      <hl-icon v-if="itemSelected">
        <system-checked />
      </hl-icon>
    </slot>
  </li>
</template>

<script lang="ts">
import {
  toRefs,
  defineComponent,
  getCurrentInstance,
  onBeforeUnmount,
  reactive,
  nextTick,
} from 'vue'
import { useId, useNamespace } from '@hongluan-ui/hooks'
import HlIcon from '@hongluan-ui/components/icon'
import { SystemChecked } from '@hongluan-ui/components/system-icon'
import { useOption } from './useOption'
import type { SelectOptionProxy } from './token'

export default defineComponent({
  name: 'Option',
  components: {
    HlIcon,
    SystemChecked,
  },
  componentName: 'Option',

  props: {
    /**
     * @description value of option
     */
    value: {
      type: [String, Number, Boolean, Object],
    },
    /**
     * @description label of option, same as `value` if omitted
     */
    label: [String, Number],
    created: Boolean,
    /**
     * @description whether option is disabled
     */
    disabled: Boolean,
    divider: Boolean,
  },

  setup(props) {
    const { namespace } = useNamespace('option')
    const id = useId()

    const states = reactive({
      index: -1,
      groupDisabled: false,
      visible: true,
      hitState: false,
      hover: false,
    })

    const {
      currentLabel,
      itemSelected,
      isDisabled,
      select,
      hoverItem,
    } = useOption(props, states)

    const {
      visible,
      hover,
    } = toRefs(states)

    const vm = getCurrentInstance().proxy
    select.onOptionCreate(vm as unknown as SelectOptionProxy)

    onBeforeUnmount(() => {
      const key = (vm as unknown as SelectOptionProxy).value
      const { selected } = select
      let selectedOptions = select.props.multiple ? selected : [selected]
      const doesSelected = selectedOptions.some(item => {
        return item.value === (vm as unknown as SelectOptionProxy).value
      })
      // if option is not selected, remove it from cache
      nextTick(() => {
        if (select.cachedOptions.get(key) === vm && !doesSelected) {
          select.cachedOptions.delete(key)
        }
      })

      select.onOptionDestroy(key, vm as unknown as SelectOptionProxy)
    })

    function selectOptionClick() {
      if (props.disabled !== true && states.groupDisabled !== true) {
        select.handleOptionSelect(vm)
      }
    }

    return {
      id,
      namespace,
      currentLabel,
      itemSelected,
      isDisabled,
      select,
      hoverItem,
      visible,
      hover,
      selectOptionClick,
      states,
    }
  },
})

</script>
